import type { VariantProps } from 'class-variance-authority';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@savvy-kit/lib/utils';
import { Tag } from '@savvy-kit/patterns/tag/tag';
import { cva } from 'class-variance-authority';
import { FileIcon, FileSpreadsheetIcon, FileTextIcon, ImageIcon, VideoIcon } from 'lucide-react';

// ============================================================================
// File type icon mapping
// ============================================================================

const IMAGE_TYPES = new Set(['JPEG', 'JPG', 'PNG', 'GIF', 'SVG', 'WEBP', 'BMP', 'TIFF', 'ICO', 'HEIC']);
const DOCUMENT_TYPES = new Set(['PDF', 'DOC', 'DOCX', 'TXT', 'RTF', 'ODT', 'PAGES']);
const SPREADSHEET_TYPES = new Set(['XLS', 'XLSX', 'CSV', 'ODS', 'NUMBERS']);
const VIDEO_TYPES = new Set(['MP4', 'MOV', 'AVI', 'MKV', 'WEBM', 'WMV']);

function getFileTypeIcon(fileType: string) {
  const type = fileType.toUpperCase();
  if (IMAGE_TYPES.has(type)) return ImageIcon;
  if (DOCUMENT_TYPES.has(type)) return FileTextIcon;
  if (SPREADSHEET_TYPES.has(type)) return FileSpreadsheetIcon;
  if (VIDEO_TYPES.has(type)) return VideoIcon;
  return FileIcon;
}

// ============================================================================
// Variants
// ============================================================================

const imageCardVariants = cva(
  'group/image-card flex flex-col overflow-hidden rounded-xl border border-light bg-slate-100',
  {
    variants: {
      size: {
        default: 'w-[271px]',
        compact: 'w-[200px]',
      },
      selected: {
        true: 'border-2 border-green-300',
        false: '',
      },
    },
    defaultVariants: {
      size: 'default',
      selected: false,
    },
  },
);

const previewVariants = cva('relative flex items-start overflow-hidden px-6 pt-6', {
  variants: {
    size: {
      default: 'h-[162px]',
      compact: 'h-[120px]',
    },
  },
  defaultVariants: {
    size: 'default',
  },
});

// ============================================================================
// FileTypeTag
// ============================================================================

function FileTypeTag({ fileType }: { fileType: string }) {
  const Icon = getFileTypeIcon(fileType);
  return (
    <Tag variant="filter" labelClassName="inline-flex items-center gap-1">
      <Icon className="size-3 shrink-0" />
      {fileType}
    </Tag>
  );
}

// ============================================================================
// ImageCard
// ============================================================================

type ImageCardProps = Omit<ComponentProps<'div'>, 'title'> &
  VariantProps<typeof imageCardVariants> & {
    readonly title: string;
    readonly fileType: string;
    readonly preview: ReactNode;
    readonly action: ReactNode;
    readonly badge?: ReactNode;
    readonly tags?: ReactNode;
  };

function ImageCard({
  className,
  size = 'default',
  selected = false,
  title,
  fileType,
  preview,
  action,
  badge,
  tags,
  ...props
}: ImageCardProps) {
  return (
    <div
      data-slot="image-card"
      className={cn(
        imageCardVariants({ size, selected }),
        props.onClick && 'cursor-pointer transition-shadow hover:shadow-md',
        className,
      )}
      {...props}
    >
      {/* Preview area */}
      <div data-slot="image-card-preview" className={cn(previewVariants({ size }))}>
        {badge && <div className="absolute left-4 top-4 z-10">{badge}</div>}
        <div className="absolute right-4 top-4 z-10">{action}</div>
        <div className="size-full overflow-hidden rounded-t-sm rounded-b-none">{preview}</div>
      </div>

      {/* Footer */}
      <div
        data-slot="image-card-footer"
        className={cn('flex flex-col gap-2 p-4', selected ? 'bg-primary-background' : 'bg-card')}
      >
        <span className="truncate text-sm font-semibold text-foreground" title={title}>
          {title}
        </span>
        <div className="flex flex-wrap items-center gap-1">
          <FileTypeTag fileType={fileType} />
          {tags}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Exports
// ============================================================================

export { ImageCard, imageCardVariants, type ImageCardProps };
